import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { FormPassEditField, InputField, OutputField, FormEmailEditField } from '../../common/Forms';
import { userActions } from '../../../actions';
import { castField, getValidationSchemaForFields } from '../../../util';

const userIdField = {
    id: 'id',
    name: 'ID',
    inputType: 'text'
};
const userEmailField = {
    id: 'email',
    name: 'Email',
    inputType: 'email'
};
const userFields = [
    {
        id: 'name',
        name: 'Отображаемое имя (никнейм)',
        inputType: 'text',
        required: true
    },
    {
        id: 'surname',
        name: 'Фамилия',
        inputType: 'rustext',
        required: true
    },
    {
        id: 'firstname',
        name: 'Имя',
        inputType: 'rustext',
        required: true
    },
    {
        id: 'patronimic',
        name: 'Отчество',
        inputType: 'rustext'
    },
    {
        id: 'gender',
        name: 'Пол',
        inputType: 'sex'
    },
    {
        id: 'birthDate',
        name: 'Дата рождения',
        inputType: 'date'
    },
    {
        id: 'city',
        name: 'Город проживания',
        inputType: 'rustext'
    },
    {
        id: 'club',
        name: 'Клуб',
        inputType: 'text'
    },
    {
        id: 'phone',
        name: 'Телефон',
        inputType: 'phone'
    },
    {
        id: 'vkUrl',
        name: 'Страница профиля ВК',
        inputType: 'vkUrl'
    }
];

function ProfilePage(props) {
    const { user, changing, errors, update, clearErrors } = props;
    const [editField, setEditField] = useState(null);

    const userSchema = useMemo(() => {
        return getValidationSchemaForFields(userFields);
    }, []);

    useEffect(() => {
        clearErrors();
    }, [editField, clearErrors]);

    useEffect(() => {
        return () => clearErrors();
    }, [clearErrors]);

    useEffect(() => {
        if (!changing && !errors) setEditField(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changing]);

    const handleEditClick = useCallback(
        (e, field) => {
            if (changing) return;
            setEditField(field.id);
        },
        [changing]
    );
    const handleCancelClick = useCallback(() => {
        if (changing) return;
        setEditField(null);
    }, [changing]);
    const handleSubmit = useCallback(
        (e, field, value) => {
            if (changing) return;
            const val = castField({ field, value });
            update({ field: field.id, value: val });
        },
        [changing, update]
    );
    const handleChangePassClick = useCallback(() => {
        if (changing) return;
        setEditField('pass');
    }, [changing]);
    const handlePassSubmit = useCallback(
        values => {
            if (changing) return;
            update({
                field: 'pass',
                value: values.pass,
                params: {
                    oldPass: values.oldPass,
                    confirmPass: values.confirmPass
                }
            });
        },
        [changing, update]
    );
    const handleChangeEmailClick = useCallback(() => {
        if (changing) return;
        setEditField('email');
    }, [changing]);
    const handleEmailSubmit = useCallback(
        values => {
            if (changing) return;
            update({
                field: 'email',
                value: values.email,
                params: {
                    pass: values.pass
                }
            });
        },
        [changing, update]
    );

    return (
        <Container className="py-5">
            <Row>
                <Col sm={6} className="mb-3">
                    <Card>
                        <Card.Header>
                            <Card.Title>Данные</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <OutputField field={userIdField} value={user.id} />
                            <OutputField field={userEmailField} value={user.email} />
                            {userFields.map(field =>
                                editField === field.id ? (
                                    <InputField
                                        key={field.id}
                                        field={field}
                                        defaultValue={user[field.id] || ''}
                                        onCancel={handleCancelClick}
                                        onSubmit={handleSubmit}
                                        schema={userSchema}
                                        submitting={changing}
                                        errors={errors}
                                        clearErrors={clearErrors}
                                    />
                                ) : (
                                    <OutputField
                                        key={field.id}
                                        field={field}
                                        value={user[field.id] || ''}
                                        onEditClick={handleEditClick}
                                    />
                                )
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col sx={12} sm={6} className="mb-3">
                    <Card>
                        <Card.Header>
                            <Card.Title>Операции</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div>
                                {editField !== 'pass' ? (
                                    <Button variant="link" onClick={handleChangePassClick}>
                                        Сменить пароль
                                    </Button>
                                ) : (
                                    <FormPassEditField
                                        onCancel={handleCancelClick}
                                        onSubmit={handlePassSubmit}
                                        schema={userSchema}
                                        errors={errors}
                                        clearErrors={clearErrors}
                                    />
                                )}
                            </div>
                            <div>
                                {editField !== 'email' ? (
                                    <Button variant="link" onClick={handleChangeEmailClick}>
                                        Сменить email
                                    </Button>
                                ) : (
                                    <FormEmailEditField
                                        onCancel={handleCancelClick}
                                        onSubmit={handleEmailSubmit}
                                        schema={userSchema}
                                        errors={errors}
                                        clearErrors={clearErrors}
                                    />
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

function mapStateToProps(state) {
    const {
        auth: { user, changing, errors }
    } = state;
    return { user, changing, errors };
}

const actionsToProps = {
    update: userActions.update,
    clearErrors: userActions.clearErrors
};

export default connect(mapStateToProps, actionsToProps)(ProfilePage);
